import React, {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import ActionMenu, {type IMenuAction} from "./ActionMenu";
import StructuredValue, {leafPaths, parsedStructuredString, valueAtPath} from "./StructuredValue";
import {tableFilterTemplate, type FilterTemplateType} from "../tableFilterTemplates";
import useDialogFocus from "./useDialogFocus";

export type TableFilterType = FilterTemplateType;

export interface ITableFilterConfig {
  field: string;
  label?: string;
  type?: TableFilterType;
  options?: Array<{label: string; value: string}>;
}

export interface IDataTableProps {
  rows: Array<Record<string, any>>;
  datasetKey: string;
  defaultColumns?: string[];
  detailsTitle?: string;
  userTimeZone?: string;
  totalCount?: number;
  serverFilter?: {field: string; value: string; onChange: (next: {field: string; value: string}) => void};
  filters?: ITableFilterConfig[];
  rowActions?: (record: Record<string, any>) => IMenuAction[];
  detailActions?: (record: Record<string, any>) => IMenuAction[];
  detailsRenderer?: (record: Record<string, any>) => React.ReactNode;
  onRowAction?: (actionId: string, record: Record<string, any>) => void;
  includeViewAction?: boolean;
}

const COLUMN_LIMIT = 5;
const EMPTY_COLUMNS: string[] = [];
const columnPriority = ["_id", "id", "userId", "email", "name", "title", "key", "type", "status", "createdAt", "updatedAt"];

const storageKey = (datasetKey: string) => `admin-id-express-table-columns:${datasetKey}`;
const serialize = (value: any) => typeof value === "object" && value !== null ? JSON.stringify(value) : String(value ?? "");

const orderedColumns = (rows: Array<Record<string, any>>) => {
  const available = Array.from(new Set(rows.flatMap(row => Object.keys(row || {}))));
  const nested = Array.from(new Set(rows.flatMap(row => leafPaths(row).filter(path => path.includes(".")))));
  return [
    ...columnPriority.filter(column => available.includes(column)),
    ...available.filter(column => !columnPriority.includes(column)),
    ...nested,
  ];
};

export const defaultVisibleColumns = (available: string[], preferred: string[] = []) => {
  const selected = [...preferred.filter(column => available.includes(column)), ...available.filter(column => !preferred.includes(column))];
  return Array.from(new Set(selected)).slice(0, COLUMN_LIMIT);
};

const storedColumns = (datasetKey: string, available: string[], fallback: string[]) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey(datasetKey)) || "[]");
    const valid = Array.isArray(parsed) ? parsed.filter(column => typeof column === "string" && available.includes(column)) : [];
    return valid.length ? valid : fallback;
  } catch {
    return fallback;
  }
};

const statusTranslationKeys: Record<string, string> = {
  active: "identityUsers.statusActive",
  inactive: "identityUsers.statusInactive",
  blocked: "identityUsers.statusBlocked",
  deleted: "identityUsers.statusDeleted",
  expired: "tokenExpired",
  refreshable: "tokenRefreshable",
  unknown: "tokenUnknown",
  identity: "kyc.identityTitle",
  residence: "kyc.residenceTitle",
  pending: "kyc.status.pending",
  verified: "kyc.status.verified",
  rejected: "kyc.status.rejected",
};

export default function DataTable({rows, datasetKey, defaultColumns, detailsTitle, userTimeZone, totalCount, serverFilter, filters, rowActions, detailActions, detailsRenderer, onRowAction, includeViewAction = true}: IDataTableProps) {
  const {t, i18n} = useTranslation();
  const available = useMemo(() => {
    const preferred = defaultColumns || EMPTY_COLUMNS;
    return Array.from(new Set([...preferred, ...orderedColumns(rows)]));
  }, [defaultColumns, rows]);
  const defaults = useMemo(() => defaultVisibleColumns(available, defaultColumns || EMPTY_COLUMNS), [available, defaultColumns]);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => storedColumns(datasetKey, available, defaults));
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [filterField, setFilterField] = useState("*");
  const [filterValue, setFilterValue] = useState("");
  const [inspecting, setInspecting] = useState<Record<string, any> | null>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const optionsDialogRef = useDialogFocus<HTMLDivElement>(optionsOpen, () => setOptionsOpen(false));
  const detailsDialogRef = useDialogFocus<HTMLDivElement>(Boolean(inspecting), () => setInspecting(null));

  useEffect(() => {
    setVisibleColumns(storedColumns(datasetKey, available, defaults));
    if (!serverFilter) {
      setFilterField("*");
      setFilterValue("");
    }
  }, [datasetKey]);

  useEffect(() => {
    setVisibleColumns(current => {
      const valid = current.filter(column => available.includes(column));
      return valid.length ? valid : defaults;
    });
  }, [available, defaults]);

  useEffect(() => {
    if (!inspecting) return;
    const identity = inspecting._id || inspecting.id || inspecting.userId;
    const current = rows.find(row => (row._id || row.id || row.userId) === identity);
    if (current && current !== inspecting) setInspecting(current);
  }, [rows, inspecting]);

  useEffect(() => {
    if (!visibleColumns.length) return;
    try { localStorage.setItem(storageKey(datasetKey), JSON.stringify(visibleColumns)); } catch { /* Storage can be unavailable in private mode. */ }
  }, [datasetKey, visibleColumns]);

  useEffect(() => {
    if (!optionsOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (!optionsRef.current?.contains(event.target as Node)) setOptionsOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOptionsOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [optionsOpen]);

  const filterDefinitions = useMemo<ITableFilterConfig[]>(() => filters?.length ? filters : available.map(field => {
    const template = tableFilterTemplate(datasetKey, field);
    return {
      field,
      type: template.type,
      options: template.values?.map(value => ({
        value,
        label: statusTranslationKeys[value] ? t(statusTranslationKeys[value]) : value,
      })),
    };
  }), [available, datasetKey, filters, i18n.resolvedLanguage, i18n.language, t]);
  useEffect(() => {
    if (filterField !== "*" && !filterDefinitions.some(filter => filter.field === filterField)) {
      setFilterField("*");
      setFilterValue("");
    }
  }, [filterDefinitions, filterField]);
  const effectiveFilterField = serverFilter?.field ?? filterField;
  const effectiveFilterValue = serverFilter?.value ?? filterValue;
  const activeFilter = filterDefinitions.find(filter => filter.field === effectiveFilterField);
  const filteredRows = useMemo(() => {
    if (serverFilter) return rows;
    const needle = effectiveFilterValue.trim().toLowerCase();
    if (!needle) return rows;
    if (effectiveFilterField === "*") return rows.filter(row => available.some(column => serialize(row?.[column]).toLowerCase().includes(needle)));
    return rows.filter(row => {
      const value = valueAtPath(row, effectiveFilterField);
      if (activeFilter?.type === "boolean") return String(Boolean(value)) === needle;
      if (activeFilter?.type === "number") return Number(value) === Number(effectiveFilterValue);
      if (activeFilter?.type === "select") return String(value) === effectiveFilterValue;
      return serialize(value).toLowerCase().includes(needle);
    });
  }, [activeFilter, available, effectiveFilterField, effectiveFilterValue, rows, serverFilter]);

  const toggleColumn = (column: string) => setVisibleColumns(current => current.includes(column)
    ? current.length === 1 ? current : current.filter(item => item !== column)
    : [...current, column]);

  const changeFilterField = (field: string) => {
    if (serverFilter) serverFilter.onChange({field, value: ""});
    else { setFilterField(field); setFilterValue(""); }
  };
  const changeFilterValue = (value: string) => {
    if (serverFilter) serverFilter.onChange({field: effectiveFilterField, value});
    else setFilterValue(value);
  };

  const actionsFor = (record: Record<string, any>) => {
    const configured = rowActions?.(record) || [];
    return [
      ...(includeViewAction ? [{id: "view", label: t("dataTable.view"), icon: "eye"}] : []),
      ...(includeViewAction && configured.length ? [{type: "divider" as const}] : []),
      ...configured,
    ];
  };

  const handleRowAction = (actionId: string, record: Record<string, any>) => {
    if (actionId === "view") { setInspecting(record); return; }
    onRowAction?.(actionId, record);
  };

  const fieldLabel = (column: string) => t(`dataTable.fields.${column}.label`, {
    defaultValue: column.split(".").map(part => t(`dataTable.fields.${part}.label`, {
      defaultValue: part.replaceAll("_", " ").replace(/([a-z])([A-Z])/g, "$1 $2"),
    })).join(" · "),
  });
  const fieldDescription = (column: string) => t(`dataTable.fields.${column}.description`, {defaultValue: fieldLabel(column)});
  const dateValue = (column: string, value: any) => {
    if (column === "sessionDuration" || typeof value !== "string" || !/(?:at|timestamp|date|time)$/i.test(column)) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const locale = i18n.resolvedLanguage || i18n.language || "en";
    const options = {dateStyle: "medium", timeStyle: "medium"} as const;
    const system = new Intl.DateTimeFormat(locale, {...options, timeZone: "UTC"}).format(date);
    const zone = userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    let user = system;
    try { user = new Intl.DateTimeFormat(locale, {...options, timeZone: zone}).format(date); } catch { /* Invalid profile timezone falls back to UTC. */ }
    return <span className="dual-time"><span><b>{t("dataTable.systemTime")}</b>{system} UTC</span><span><b>{t("dataTable.yourTime", {defaultValue: t("dataTable.userTime")})}</b>{user} <small>{zone}</small></span></span>;
  };
  const cell = (column: string, value: any) => {
    if (["status", "accessTokenStatus", "refreshTokenStatus"].includes(column) && statusTranslationKeys[String(value)]) return <span className={`token-state ${value}`}>{t(statusTranslationKeys[String(value)])}</span>;
    const date = dateValue(column, value);
    if (date) return date;
    if (["action", "activityType", "eventType"].includes(column) && value !== null && value !== undefined) {
      const eventType = String(value);
      const fallback = eventType.replace(/[._-]+/g, " ").replace(/\b\w/g, letter => letter.toUpperCase());
      return <span title={eventType}>{t(`activityEvents.${eventType}`, {defaultValue: fallback})}</span>;
    }
    if (["service", "resource"].includes(column) && value !== null && value !== undefined) return <span title={String(value)}>{t(`dataTable.values.${column}.${String(value)}`, {defaultValue: String(value).replaceAll("_", " ")})}</span>;
    if (column === "message" && value !== null && value !== undefined) return <span className="log-message">{String(value)}</span>;
    if (["traceback", "details"].includes(column) && value !== null && value !== undefined) return <pre className="log-details" title={fieldDescription(column)}>{String(value)}</pre>;
    if (parsedStructuredString(value)) return <StructuredValue value={value} compact/>;
    return typeof value === "object" && value !== null ? <StructuredValue value={value} compact/> : String(value ?? "—");
  };

  return <div className="data-table">
    <div className="table-controls">
      <div className="table-filter-group">
        <span className="filter-leading" aria-hidden="true">⌕</span>
        <select value={effectiveFilterField} onChange={event => changeFilterField(event.target.value)} title={t("dataTable.filterField")} aria-label={t("dataTable.filterField")}>
          <option value="*">{t("dataTable.allFields")}</option>
          {filterDefinitions.map(filter => <option key={filter.field} value={filter.field}>{filter.label || fieldLabel(filter.field)}</option>)}
        </select>
        {activeFilter?.type === "select" ? <select value={effectiveFilterValue} onChange={event => changeFilterValue(event.target.value)} title={t("dataTable.filterValue")} aria-label={t("dataTable.filterValue")}>
          <option value="">{t("dataTable.allValues")}</option>{activeFilter.options?.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select> : activeFilter?.type === "boolean" ? <select value={effectiveFilterValue} onChange={event => changeFilterValue(event.target.value)} title={t("dataTable.filterValue")} aria-label={t("dataTable.filterValue")}>
          <option value="">{t("dataTable.allValues")}</option><option value="true">true</option><option value="false">false</option>
        </select> : <label className="table-filter"><input key={`${effectiveFilterField}:${activeFilter?.type || "text"}`} type={activeFilter?.type === "number" ? "number" : activeFilter?.type === "date" ? "date" : "search"} value={effectiveFilterValue} onChange={event => changeFilterValue(event.target.value)} placeholder={t("searchDataset")} aria-label={t("dataTable.filterValue")}/></label>}
        {effectiveFilterValue && <button type="button" className="secondary table-reset" title={t("dataTable.reset")} onClick={() => changeFilterValue("")}>× {t("dataTable.reset")}</button>}
      </div>
      <div className="column-options" ref={optionsRef}>
        <button type="button" className="secondary column-options-trigger" title={t("dataTable.columnsHelp")} aria-expanded={optionsOpen} aria-haspopup="dialog" onClick={() => setOptionsOpen(value => !value)}>▦ {t("dataTable.columns")} <b>{visibleColumns.length}</b></button>
        {optionsOpen && <div ref={optionsDialogRef} tabIndex={-1} className="column-options-panel" role="dialog" aria-modal="true" aria-label={t("dataTable.columns")}>
          <div><b>{t("dataTable.columns")}</b><small>{t("dataTable.columnsHelp")}</small></div>
          <div className="column-checkboxes">{available.map(column => <label key={column} title={fieldDescription(column)}><input type="checkbox" checked={visibleColumns.includes(column)} disabled={visibleColumns.length === 1 && visibleColumns.includes(column)} onChange={() => toggleColumn(column)}/><span>{fieldLabel(column)}</span></label>)}</div>
          <button type="button" className="secondary" title={t("dataTable.reset")} onClick={() => setVisibleColumns(defaults)}>{t("dataTable.reset")}</button>
        </div>}
      </div>
    </div>
    {effectiveFilterValue && <div className="table-filter-summary" role="status">{t("dataTable.found", {count: totalCount ?? filteredRows.length})}</div>}
    {filteredRows.length ? <div className="table-shell"><table><thead><tr><th className="action-menu-column">{t("administrators.actions")}</th>{visibleColumns.map(column => <th key={column} title={fieldDescription(column)}>{fieldLabel(column)}</th>)}</tr></thead><tbody>{filteredRows.map((row, index) => <tr key={row?.id || row?._id || index}><td className="action-menu-cell"><ActionMenu actions={actionsFor(row)} record={row} onAction={handleRowAction} triggerLabel={`${t("administrators.actions")}: ${row?.id || row?._id || index + 1}`}/></td>{visibleColumns.map(column => <td key={column}>{cell(column, valueAtPath(row, column))}</td>)}</tr>)}</tbody></table></div> : <div className="empty compact"><b>{t("noRecords")}</b><p>{t("noVisibleData")}</p></div>}
    {inspecting && <div className="modal-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) setInspecting(null); }}><div ref={detailsDialogRef} tabIndex={-1} className="modal record-details" role="dialog" aria-modal="true" aria-labelledby="record-details-title"><button type="button" className="modal-close" title={t("administrators.close")} aria-label={t("administrators.close")} onClick={() => setInspecting(null)}>×</button><span className="eyebrow">{datasetKey}</span><div className="record-details-heading"><h2 id="record-details-title">{detailsTitle || t("dataTable.view")}</h2><div>{(detailActions?.(inspecting) || []).filter(action => action.type !== "divider" && action.id).map(action => <button key={action.id} type="button" className={action.isDanger ? "danger-button" : "primary"} title={action.label} onClick={() => action.id && onRowAction?.(action.id, inspecting)}>{action.label}</button>)}</div></div>{detailsRenderer ? detailsRenderer(inspecting) : <div className="record-details-grid">{Object.entries(inspecting).map(([field, value]) => <div key={field}><b title={fieldDescription(field)}>{fieldLabel(field)}</b><span>{cell(field, value)}</span></div>)}</div>}</div></div>}
  </div>;
}
